import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { FiBook, FiMessageCircle, FiSend, FiCheckCircle, FiHelpCircle, FiBarChart2, FiUser, FiTrendingUp, FiClock, FiSearch, FiChevronDown, FiChevronRight, FiZap, FiTarget, FiLayers, FiCompass, FiAward, FiPlay, FiBookOpen, FiLifeBuoy } from 'react-icons/fi';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const THEME = {
  primary: '#00B4D8', secondary: '#48CAE4', accent: '#90E0EF',
  success: '#10B981', warning: '#F59E0B', danger: '#EF4444',
  dark: '#0A1628', muted: '#64748B', purple: '#8B5CF6', pink: '#EC4899'
};

/* ── Animated Counter ── */
const AnimatedNumber = ({ value, suffix = '' }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const num = typeof value === 'number' ? value : parseInt(String(value).replace(/[^0-9]/g, ''), 10) || 0;
    if (num === 0) { setDisplay(0); return; }
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / 800, 1);
      setDisplay(Math.floor(progress * num));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]);
  return <>{display.toLocaleString()}{suffix}</>;
};

/* ── Circular Stat ── */
const CircularStat = ({ value, max, label, color, icon }) => {
  const percentage = max > 0 ? Math.round((value / max) * 100) : 0;
  const circumference = 2 * Math.PI * 36;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ position: 'relative', width: 88, height: 88, margin: '0 auto' }}>
        <svg width={88} height={88} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={44} cy={44} r={36} fill="none" stroke="rgba(0,180,216,0.1)" strokeWidth={6} />
          <circle cx={44} cy={44} r={36} fill="none" stroke={color} strokeWidth={6}
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 1s ease' }} />
        </svg>
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          color, fontSize: 20
        }}>{icon}</div>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: THEME.dark, marginTop: 8 }}>
        <AnimatedNumber value={value} />
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
    </div>
  );
};

/* ── Quick Action Card ── */
const QuickActionCard = ({ label, icon, path, color, description, delay }) => (
  <Link to={path} style={{ textDecoration: 'none' }}>
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)', border: '1px solid var(--border-color)',
      transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
      animation: `slideUp 0.5s ease ${delay}s both`, position: 'relative', overflow: 'hidden'
    }}
      onMouseEnter={e => { 
        e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)'; 
        e.currentTarget.style.boxShadow = `0 16px 40px ${color}20`;
        e.currentTarget.style.borderColor = color;
      }}
      onMouseLeave={e => { 
        e.currentTarget.style.transform = ''; 
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
        e.currentTarget.style.borderColor = 'var(--border-color)';
      }}>
      <div style={{
        position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%',
        background: `${color}10`, filter: 'blur(30px)'
      }} />
      <div style={{
        width: 56, height: 56, borderRadius: 16, background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
        boxShadow: `0 8px 20px ${color}30`
      }}>
        {React.cloneElement(icon, { size: 24, color: '#fff' })}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: THEME.dark, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: THEME.muted }}>{description}</div>
      <div style={{
        position: 'absolute', bottom: 16, right: 16, width: 32, height: 32, borderRadius: 8,
        background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color, transition: 'all 0.3s ease'
      }}><FiChevronRight size={16} /></div>
    </div>
  </Link>
);

/* ── FAQ Accordion Item ── */
const FAQItem = ({ item, color, isOpen, onToggle, index }) => (
  <div style={{
    borderRadius: 14, border: `1px solid ${isOpen ? color : 'var(--border-color)'}`,
    overflow: 'hidden', transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
    boxShadow: isOpen ? `0 8px 24px ${color}15` : 'none',
    transform: isOpen ? 'scale(1.01)' : 'scale(1)',
    animation: `slideUp 0.4s ease ${0.05 + index * 0.03}s both`
  }}>
    <button onClick={onToggle} style={{
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '18px 20px', background: isOpen ? `${color}08` : 'var(--bg-primary)',
      border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all 0.3s ease'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: `${color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'none'
        }}>
          <FiHelpCircle size={16} color={color} />
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, color: THEME.dark }}>{item.q}</span>
      </div>
      <div style={{
        width: 28, height: 28, borderRadius: 8, background: `${color}10`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.3s ease', transform: isOpen ? 'rotate(180deg)' : 'none'
      }}>
        <FiChevronDown size={14} color={color} />
      </div>
    </button>
    {isOpen && (
      <div style={{
        padding: '0 20px 20px 70px', background: 'var(--bg-primary)',
        animation: 'slideUp 0.3s ease both'
      }}>
        <div style={{
          fontSize: 13, color: THEME.muted, lineHeight: 1.8, padding: '16px 20px',
          background: 'var(--bg-secondary)', borderRadius: 12, borderLeft: `3px solid ${color}`
        }}>{item.a}</div>
      </div>
    )}
  </div>
);

/* ── Performance Radar Chart ── */
const PerformanceRadar = ({ stats }) => {
  const data = [
    { subject: 'Campaigns', value: Math.min((stats.totalCampaigns || 0) * 20, 100), fullMark: 100 },
    { subject: 'Success', value: stats.successRate || 0, fullMark: 100 },
    { subject: 'Delivery', value: Math.min((stats.totalSent || 0) / 100, 100), fullMark: 100 },
    { subject: 'Activity', value: Math.min((stats.totalCampaigns || 0) * 25, 100), fullMark: 100 },
    { subject: 'Efficiency', value: stats.successRate || 50, fullMark: 100 }
  ];

  return (
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)', height: '100%'
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiTarget size={18} color={THEME.primary} /> Performance Overview
      </h3>
      <ResponsiveContainer width="100%" height={220}>
        <RadarChart data={data}>
          <PolarGrid stroke="rgba(0,180,216,0.2)" />
          <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: THEME.muted }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
          <Radar name="Performance" dataKey="value" stroke={THEME.primary} fill={THEME.primary} fillOpacity={0.3} strokeWidth={2} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Progress Distribution Bar ── */
const ProgressDistribution = ({ stats }) => {
  const data = [
    { name: 'Sent', value: stats.totalSent || 0, color: THEME.success },
    { name: 'Failed', value: stats.totalFailed || 0, color: THEME.danger },
    { name: 'Pending', value: stats.totalPending || 0, color: THEME.warning }
  ];
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;

  return (
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiLayers size={18} color={THEME.secondary} /> Message Distribution
      </h3>
      <div style={{ height: 12, borderRadius: 6, background: 'var(--bg-secondary)', display: 'flex', overflow: 'hidden', marginBottom: 16 }}>
        {data.map((d, i) => (
          <div key={i} style={{
            width: `${(d.value / total) * 100}%`, height: '100%', background: d.color,
            transition: 'width 1s ease'
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        {data.map((d, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', marginBottom: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
              <span style={{ fontSize: 12, color: THEME.muted }}>{d.name}</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: THEME.dark }}>{d.value.toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function UserHelp() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    api.get('/user/help-content')
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const toggleItem = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) return (
    <div className="loading-spinner">
      <div className="spinner" />
      <div className="loading-text">Loading help center...</div>
      <div className="loading-dots"><span /><span /><span /></div>
    </div>
  );

  const sections = data?.sections || [];
  const qs = data?.quickStats || {};

  const filteredSections = searchQuery
    ? sections.map(section => ({
        ...section,
        items: (section.items || []).filter(item =>
          item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.items.length > 0)
    : sections;

  const totalQuestions = sections.reduce((sum, s) => sum + (s.items?.length || 0), 0);

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
            }}><FiLifeBuoy size={22} /></span>
            Help Center
          </h1>
          <p className="page-subtitle">Everything you need to succeed with CONQUER</p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 12,
          background: 'linear-gradient(135deg, rgba(0,180,216,0.1) 0%, rgba(72,202,228,0.1) 100%)',
          border: '1px solid rgba(0,180,216,0.2)'
        }}>
          <FiBookOpen size={18} color={THEME.primary} />
          <span style={{ fontSize: 14, fontWeight: 700, color: THEME.primary }}>{totalQuestions} Topics</span>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{
        background: 'var(--bg-primary)', borderRadius: 16, padding: '20px 24px', marginBottom: 24,
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
      }}>
        <div style={{ position: 'relative', maxWidth: 600, margin: '0 auto' }}>
          <FiSearch size={20} style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: THEME.muted }} />
          <input type="text" className="form-input" placeholder="Search for help topics, guides, and answers..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            style={{
              paddingLeft: 52, width: '100%', fontSize: 15, padding: '16px 20px 16px 52px',
              borderRadius: 14, background: 'var(--bg-secondary)', border: '2px solid var(--border-color)',
              transition: 'all 0.3s ease'
            }} />
        </div>
      </div>

      {/* Stats Overview */}
      {qs.totalCampaigns !== undefined && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24,
          padding: 24, borderRadius: 16, background: 'var(--bg-primary)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
        }}>
          <CircularStat value={qs.totalCampaigns || 0} max={10} label="Campaigns" color={THEME.primary} icon={<FiLayers />} />
          <CircularStat value={qs.totalSent || 0} max={10000} label="Sent" color={THEME.success} icon={<FiSend />} />
          <CircularStat value={qs.totalFailed || 0} max={1000} label="Failed" color={THEME.danger} icon={<FiBookOpen />} />
          <CircularStat value={qs.totalPending || 0} max={5000} label="Pending" color={THEME.warning} icon={<FiClock />} />
          <CircularStat value={qs.successRate || 0} max={100} label="Success %" color={THEME.purple} icon={<FiAward />} />
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        <QuickActionCard label="My Outreach" icon={<FiSend />} path="/user/campaigns" color={THEME.primary} description="View all campaigns" delay={0.05} />
        <QuickActionCard label="Analytics" icon={<FiBarChart2 />} path="/user/analytics" color={THEME.purple} description="Performance insights" delay={0.1} />
        <QuickActionCard label="Performance" icon={<FiTrendingUp />} path="/user/performance" color={THEME.success} description="Track your metrics" delay={0.15} />
        <QuickActionCard label="Activity Log" icon={<FiClock />} path="/user/activity" color={THEME.warning} description="Login history" delay={0.2} />
        <QuickActionCard label="My Account" icon={<FiUser />} path="/user/profile" color={THEME.pink} description="Profile settings" delay={0.25} />
      </div>

      {/* Charts Row */}
      {qs.totalCampaigns !== undefined && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 20, marginBottom: 24 }}>
          <PerformanceRadar stats={qs} />
          <ProgressDistribution stats={qs} />
        </div>
      )}

      {/* FAQ Sections */}
      {filteredSections.length > 0 ? filteredSections.map((section, si) => (
        <div key={si} style={{
          background: 'var(--bg-primary)', borderRadius: 16, marginBottom: 20, overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)', animation: `slideUp 0.5s ease ${0.1 + si * 0.05}s both`
        }}>
          <div style={{
            padding: '20px 24px', borderBottom: '1px solid var(--border-color)',
            background: `linear-gradient(135deg, ${section.color || THEME.primary}08 0%, transparent 100%)`
          }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: THEME.dark, display: 'flex', alignItems: 'center', gap: 12, margin: 0 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12, background: `${section.color || THEME.primary}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: section.color || THEME.primary
              }}><FiBook size={18} /></div>
              {section.title}
              <span style={{
                marginLeft: 'auto', padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: `${section.color || THEME.primary}10`, color: section.color || THEME.primary
              }}>{section.items?.length} questions</span>
            </h3>
          </div>
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(section.items || []).map((item, i) => (
              <FAQItem key={i} item={item} color={section.color || THEME.primary}
                isOpen={!!openItems[`${si}-${i}`]}
                onToggle={() => toggleItem(si, i)} index={i} />
            ))}
          </div>
        </div>
      )) : (
        <div style={{
          background: 'var(--bg-primary)', borderRadius: 16, padding: '60px 40px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)', textAlign: 'center'
        }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(0,180,216,0.1) 0%, rgba(72,202,228,0.1) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
          }}><FiSearch size={32} color={THEME.muted} /></div>
          <h3 style={{ color: THEME.dark, marginBottom: 8, fontSize: 18, fontWeight: 700 }}>
            {searchQuery ? 'No results found' : 'No help content available'}
          </h3>
          <p style={{ color: THEME.muted, fontSize: 14 }}>
            {searchQuery ? 'Try different keywords' : 'Start by completing some campaigns to see personalized tips.'}
          </p>
        </div>
      )}

      {/* Support Footer */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(0,180,216,0.1) 0%, rgba(139,92,246,0.1) 100%)',
        borderRadius: 16, padding: '40px 32px', textAlign: 'center',
        border: '1px solid rgba(0,180,216,0.2)'
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #00B4D8 0%, #8B5CF6 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
          boxShadow: '0 12px 32px rgba(0,180,216,0.3)'
        }}><FiCompass size={32} color="#fff" /></div>
        <h3 style={{ fontSize: 20, fontWeight: 800, color: THEME.dark, marginBottom: 8 }}>Need More Assistance?</h3>
        <p style={{ fontSize: 14, color: THEME.muted, maxWidth: 500, margin: '0 auto' }}>
          Our support team is here to help you conquer your communication goals. Contact your administrator for additional support or feature requests.
        </p>
      </div>
    </div>
  );
}
